import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation, useNavigate, NavigateFunction } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { getLastRequestId, isApiError } from '@/lib/api';
import { errorTracking } from '@/observability/error-tracking';
import { BRAND_BASE_DOMAIN } from '@easyflow/shared';

interface BaseProps {
  children: ReactNode;
  fallback?: ReactNode;
  tenantSlug?: string;
}

interface InnerProps extends BaseProps {
  pathname: string;
  navigate: NavigateFunction;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  capturedAt: string | null;
}

const CHUNK_RELOAD_FLAG = 'edgvoip:chunk_reload_attempt';
const CHUNK_RELOAD_GRACE_MS = 30_000;

function isChunkLoadError(err: Error | null | undefined): boolean {
  if (!err) return false;
  const name = err.name || '';
  const msg = err.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

class ErrorBoundaryClass extends Component<InnerProps, State> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, capturedAt: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      '[ErrorBoundary] Errore catturato:',
      `MESSAGE=${error?.message ?? '(no message)'}`,
      `NAME=${error?.name ?? '(no name)'}`,
      `STACK=${error?.stack ?? '(no stack)'}`,
      `COMPONENT_STACK=${errorInfo?.componentStack ?? '(no component stack)'}`,
    );

    this.setState({ errorInfo, capturedAt: this.props.pathname });

    // Task #2604 — invio a GlitchTip (no-op se DSN vuoto). I chunk-load error
    // sono transitori (deploy in corso) → non li tracciamo come crash app.
    if (!isChunkLoadError(error)) {
      errorTracking.captureException(error, {
        tags: { boundary: 'tenant', pathname: this.props.pathname },
        extra: { componentStack: errorInfo?.componentStack ?? null },
      });
    }

    if (isChunkLoadError(error)) {
      try {
        const raw = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
        const last = raw ? Number(raw) : 0;
        const now = Date.now();
        if (!last || now - last > CHUNK_RELOAD_GRACE_MS) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(now));
          window.location.reload();
        }
      } catch {
        /* sessionStorage non disponibile: mostra fallback */
      }
    }
  }

  componentDidUpdate(prevProps: InnerProps) {
    if (
      this.state.hasError &&
      prevProps.pathname !== this.props.pathname &&
      this.state.capturedAt !== this.props.pathname
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null, capturedAt: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, capturedAt: null });
  };

  handleGoHome = () => {
    const slug = this.props.tenantSlug || localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG) || '';
    // Reset error state and navigate via SPA so the auth/session is preserved
    // (no hard reload, no logout). Falls back to root when there is no
    // tenant context yet (e.g. login screen pre-auth).
    this.setState({ hasError: false, error: null, errorInfo: null, capturedAt: null });
    if (slug) {
      this.props.navigate(`/${slug}/dashboard`, { replace: true });
    } else {
      this.props.navigate('/', { replace: true });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Qualcosa è andato storto
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Si è verificato un errore imprevisto. Puoi riprovare o tornare alla dashboard.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-red-700 font-mono break-all">
                  {this.state.error.message}
                </p>
                {/* Task #1245 — surface request id (from the error itself
                    or the last known one captured by apiClient) so the
                    user can quote it to support in 1 click. */}
                {(() => {
                  const errRid = isApiError(this.state.error) ? this.state.error.requestId : undefined;
                  const rid = errRid || getLastRequestId();
                  if (!rid) return null;
                  const short = rid.length > 8 ? rid.slice(0, 8) : rid;
                  return (
                    <p className="text-[10px] text-red-600/70 font-mono mt-1">
                      Riferimento: {short}
                    </p>
                  );
                })()}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <RefreshCw className="h-4 w-4" />
                Riprova
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <Home className="h-4 w-4" />
                Torna alla Dashboard
              </button>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              Se il problema persiste, contatta il supporto a{' '}
              <a href={`mailto:support@${BRAND_BASE_DOMAIN}`} className="text-blue-500 hover:underline">
                support@{BRAND_BASE_DOMAIN}
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function ErrorBoundary(props: BaseProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return <ErrorBoundaryClass {...props} pathname={pathname} navigate={navigate} />;
}

export default ErrorBoundary;
